import { Box, Grid, TextField } from "@mui/material";
import { blue } from "@mui/material/colors";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { GlobalBox } from "components/Box/GlobalBox";
import RegularButton from "components/CustomButtons/Button";
import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { ivaOptions, decimalsOptions, conceptOpt } from "helpers/dictionary";
import { useAddNotesHook } from "./useAddNotesHooks";
import { newDateWithTime } from "helpers/dateTools";
import { reasonNotesOptions, addNotesConfig } from "./config";

export const AddNotes = () => {
  const {
    handleGetNoteUnification,
    handleRecalculate,
    handleSubmit,
    isDataChanged,
    values,
    notes,
    md,
    errors,
    onChange,
    onChangeNumber,
    setValues,
    isLoadingComp,
    billingReasons,
    handleChangeReason,
    handleDate,
  } = useAddNotesHook();

  const {
    id,
    date,
    blockDate,
    invoiceKey,
    folio,
    quantity,
    price,
    subTotal,
    iva,
    grouped,
    total,
    idReason,
    decimals,
    block,
    messageFolio,
    discountAmount,
    discountType,
    subTotalWithDiscount,
    totalWithDiscount,
  } = values;
  const currentDay = new Date().getDate()
  const isAvailable = currentDay >= 1 && currentDay <= 3
  const discountReason = idReason?.description === reasonNotesOptions.discount
  const buildingsReason = idReason?.description === reasonNotesOptions.buildings
  return (
    <Grid item xs={12} md={4} component="form">
      <GlobalBox
        title={addNotesConfig.title}
        head
        style={{ boxShadow: "none", margin: 0 }}
        styleBody={{ paddingLeft: 0, paddingRight: 0 }}
      >
        <TextField
          type={addNotesConfig.fields.date.type}
          variant="outlined"
          label={addNotesConfig.fields.date.label}
          name={addNotesConfig.fields.date.name}
          value={date}
          onChange={handleDate}
          fullWidth
          inputProps={{
            max: newDateWithTime(),
            min: newDateWithTime({
              diffDays: isAvailable ? 0 : -3,
            }),
          }}
          disabled={blockDate}
        />
        <AutocompleteComponent
          label={addNotesConfig.fields.idReason.label}
          options={billingReasons}
          search=""
          name={addNotesConfig.fields.idReason.name}
          value={idReason}
          handleValues={handleChangeReason}
          textFieldProps={{
            error: errors.idReason,
            helperText: errors.idReason,
          }}
          disabled={notes.length}
        />
        <TextField
          variant="outlined"
          label={addNotesConfig.fields.invoiceKey.label}
          name={addNotesConfig.fields.invoiceKey.name}
          value={invoiceKey}
          onChange={onChange}
          fullWidth
          disabled={idReason?.description === reasonNotesOptions.advances || isDataChanged}
        />
        <TextField
          variant="outlined"
          label={addNotesConfig.fields.folio.label}
          name={addNotesConfig.fields.folio.name}
          value={folio}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "");
            setValues({ folio: onlyDigits });
          }}
          fullWidth
          InputProps={{
            inputProps: { right: "1" },
            endAdornment: idReason?.description !== reasonNotesOptions.advances && (
              <RegularButton
                justIcon
                round
                color="transparent"
                tooltip={addNotesConfig.tooltips.searchNote}
                onClick={handleGetNoteUnification}
                loading={isLoadingComp?.loadingInvoicesUnificationNote}
              >
                <SearchIcon
                  sx={{
                    color: blue[500],
                  }}
                />
              </RegularButton>
            ),
          }}
          error={errors.folio || messageFolio}
          helperText={errors.folio || messageFolio}
          disabled={isDataChanged}
        />
        <Grid container spacing={1} style={{ paddingRight: 0 }}>
          {discountReason &&
            <Grid item xs={12} md={6} style={{ paddingRight: 0 }}>
              <TextField
                variant="outlined"
                label={addNotesConfig.fields.discount.label}
                name={addNotesConfig.fields.discount.name}
                value={discountAmount}
                fullWidth
                disabled
                inputProps={{ right: "1" }}
                InputProps={{
                  endAdornment: <div style={{ marginLeft: 8 }}> {discountType}</div>,
                }}
              />
            </Grid>
          }
          <Grid item xs={12} md={discountReason ? 6 : 12} style={{ paddingRight: 0 }}>
            <TextField
              variant="outlined"
              label={addNotesConfig.fields.quantity.label}
              name={addNotesConfig.fields.quantity.name}
              value={quantity}
              onChange={(_) =>
                onChangeNumber(_, {
                  decimals: buildingsReason ? decimalsOptions[1]?.value : decimalsOptions[2]?.value ,
                })}
              fullWidth
              inputProps={{ right: "1" }}
              error={errors.quantity}
              helperText={errors.quantity}
              InputProps={{
                endAdornment: <div style={{ marginLeft: 8 }}> Lts</div>,
              }}
              disabled={isDataChanged || discountReason }
            />
          </Grid>
          <Grid item xs={12} md={6} style={{ paddingRight: 0 }}>
            <AutocompleteComponent
              label={addNotesConfig.fields.decimals.label}
              options={decimalsOptions}
              search=""
              name={addNotesConfig.fields.decimals.name}
              value={decimals}
              handleValues={setValues}
              align="right"
              textFieldProps={{
                error: errors.decimals,
                helperText: errors.decimals,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} style={{ paddingRight: 0 }}>
            <TextField
              variant="outlined"
              label={addNotesConfig.fields.price.label}
              name={addNotesConfig.fields.price.name}
              value={price}
              onChange={(_) =>
                onChangeNumber(_, {
                  decimals: decimals?.value,
                })
              }
              fullWidth
              inputProps={{ right: "1" }}
              error={errors.price}
              helperText={errors.price}
              InputProps={{
                startAdornment: "$",
                endAdornment: (
                  <RegularButton
                    justIcon
                    round
                    color="transparent"
                    tooltip={addNotesConfig.tooltips.clean}
                    onClick={() => setValues({ price: "" })}
                    size="sm"
                  >
                    <CloseIcon
                      sx={{
                        color: blue[500],
                      }}
                    />
                  </RegularButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={discountReason ? 6 : 12} style={{ paddingRight: 0 }}>
            <TextField
              variant="outlined"
              label={addNotesConfig.fields.subTotal.label}
              name={addNotesConfig.fields.subTotal.name}
              value={subTotal}
              fullWidth
              inputProps={{ right: "1" }}
              disabled
              InputProps={{
                startAdornment: "$",
              }}
            />
          </Grid>
          {
            discountReason &&
            <Grid item xs={12} md={6} style={{ paddingRight: 0 }}>
              <TextField
                variant="outlined"
                label={addNotesConfig.fields.subTotalWithDiscount.label}
                name={addNotesConfig.fields.subTotalWithDiscount.name}
                value={subTotalWithDiscount}
                fullWidth
                inputProps={{ right: "1" }}
                disabled
                InputProps={{
                  startAdornment: "$",
                }}
              />
            </Grid>
          }
          <Grid item xs={12} md={4} style={{ paddingRight: 0 }}>
            <AutocompleteComponent
              label={addNotesConfig.fields.iva.label}
              options={ivaOptions}
              name={addNotesConfig.fields.iva.name}
              value={iva}
              handleValues={setValues}
              align="right"
            />
          </Grid>
          <Grid item xs={12} md={8} style={{ paddingRight: 0 }}>
            <AutocompleteComponent
              label={addNotesConfig.fields.concept.label}
              options={conceptOpt}
              name={addNotesConfig.fields.concept.name}
              value={grouped}
              handleValues={setValues}
              textFieldProps={{
                error: errors.grouped,
                helperText: errors.grouped,
              }}
              disabled={notes.length > 0 || idReason?.description === reasonNotesOptions.advances}
            />
          </Grid>
          <Grid item xs={12} md={discountReason ? 6 : 12} style={{ paddingRight: 0 }}>
            <TextField
              variant="outlined"
              label={addNotesConfig.fields.total.label}
              name={addNotesConfig.fields.total.name}
              value={total}
              fullWidth
              inputProps={{ right: "1" }}
              disabled
              InputProps={{
                startAdornment: "$",
              }}
            />
          </Grid>
          {discountReason &&
            <Grid item xs={12} md={6} style={{ paddingRight: 0 }}>
              <TextField
                variant="outlined"
                label={addNotesConfig.fields.totalWithDiscount.label}
                name={addNotesConfig.fields.totalWithDiscount.name}
                value={totalWithDiscount}
                fullWidth
                inputProps={{ right: "1" }}
                disabled
                InputProps={{
                  startAdornment: "$",
                }}
              />
            </Grid>
          }
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {
            isDataChanged ?
              <RegularButton
                color={"warning"}
                onClick={handleRecalculate}
                fullWidth={md}
              >
                {addNotesConfig.buttons.recalculate}
              </RegularButton> :
              <RegularButton
                color={"info"}
                onClick={handleSubmit}
                fullWidth={md}
                disabled={block || price < 1}
              >
                {addNotesConfig.buttons.add}
              </RegularButton>
          }
        </Box>
      </GlobalBox >
    </Grid >
  );
};
